import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../authService';
import {
  Box,
  List,
  Typography,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Collapse,
  Chip,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  WaterDrop,
  Apartment,
  BusinessCenter,
  HealthAndSafety,
  CardMembership,
  MenuBook,
  Settings,
  Logout,
  Person,
  Dashboard,
  People,
  Receipt,
  Payment,
  Savings,
  Assessment,
  Warning,
  Description,
  Calculate,
  FactCheck,
  PeopleAlt as PeopleAltIcon,
  Store,
  Badge,
  MedicalServices,
  Assignment,
  Business,
  CloudDownload,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Apps,
  ArrowBack
} from '@mui/icons-material';

import { systemAccessService, ALL_SYSTEM_IDS } from '../../services/systemAccessService';

const UnifiedSidebar = ({ user, onLogout, onCloseMobile, onOpenProfile, onOpenSettings }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperAdmin = String(user?.role || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/-/g, '') === 'superadmin';

  const [allowedSystems, setAllowedSystems] = useState(() => {
    if (isSuperAdmin) return ALL_SYSTEM_IDS;
    if (Array.isArray(user?.allowed_systems)) return user.allowed_systems;
    return [];
  });
  const [loadingPermissions, setLoadingPermissions] = useState(!isSuperAdmin);

  useEffect(() => {
    let isMounted = true;
    const loadUserPermissions = async () => {
      if (isSuperAdmin) {
        setAllowedSystems(ALL_SYSTEM_IDS);
        setLoadingPermissions(false);
        return;
      }
      setLoadingPermissions(true);
      const allowed = await systemAccessService.getAllowedSystemsForUser(user);
      if (isMounted) {
        setAllowedSystems(allowed);
        setLoadingPermissions(false);
      }
    };
    loadUserPermissions();
    return () => {
      isMounted = false;
    };
  }, [user, isSuperAdmin]);

  // Systems definition with their sub-pages
  const systems = [
    {
      id: 'water-billing',
      name: 'Water Billing',
      badge: 'Utility',
      color: '#059669',
      bgLight: '#ecfdf5',
      icon: <WaterDrop sx={{ fontSize: 20 }} />,
      basePath: '/portal/water-billing',
      pages: [
        { name: 'Dashboard', path: '/portal/water-billing/dashboard', icon: <Dashboard sx={{ fontSize: 18 }} /> },
        { name: 'Customers', path: '/portal/water-billing/customers', icon: <People sx={{ fontSize: 18 }} /> },
        { name: 'Customer Logbook', path: '/portal/water-billing/customer-logbook', icon: <MenuBook sx={{ fontSize: 18 }} /> },
        { name: 'Meter & Billing', path: '/portal/water-billing/billing', icon: <Receipt sx={{ fontSize: 18 }} /> },
        { name: 'Cashier Payments', path: '/portal/water-billing/payments', icon: <Payment sx={{ fontSize: 18 }} /> },
        { name: 'Demand Notices', path: '/portal/water-billing/notices', icon: <Warning sx={{ fontSize: 18 }} /> },
        { name: 'Online Payments', path: '/portal/water-billing/online-payments', icon: <Payment sx={{ fontSize: 18 }} /> },
        { name: 'Credit Balances', path: '/portal/water-billing/credit-management', icon: <Savings sx={{ fontSize: 18 }} /> },
        { name: 'Reading Sheets', path: '/portal/water-billing/reading-sheets', icon: <Description sx={{ fontSize: 18 }} /> },
        { name: 'Usage Anomalies', path: '/portal/water-billing/usage-anomalies', icon: <Assessment sx={{ fontSize: 18 }} /> },
        { name: 'Water Reports', path: '/portal/water-billing/reports', icon: <Assessment sx={{ fontSize: 18 }} /> },
        ...(isSuperAdmin ? [{ name: 'System Settings', path: '/portal/water-billing/settings', icon: <Settings sx={{ fontSize: 18 }} /> }] : [])
      ]
    },
    {
      id: 'property-tax',
      name: 'Real Property Tax',
      badge: 'RPT',
      color: '#4f46e5',
      bgLight: '#eef2ff',
      icon: <Apartment sx={{ fontSize: 20 }} />,
      basePath: '/portal/property-tax',
      pages: [
        { name: 'Dashboard', path: '/portal/property-tax/dashboard', icon: <Dashboard sx={{ fontSize: 18 }} /> },
        { name: 'Properties', path: '/portal/property-tax/properties', icon: <Apartment sx={{ fontSize: 18 }} /> },
        { name: 'Tax Payments', path: '/portal/property-tax/payments', icon: <Payment sx={{ fontSize: 18 }} /> },
        { name: 'Tax Calculator', path: '/portal/property-tax/calculator', icon: <Calculate sx={{ fontSize: 18 }} /> },
        { name: 'Assessments', path: '/portal/property-tax/assessments', icon: <Assessment sx={{ fontSize: 18 }} /> },
        { name: 'Property Owners', path: '/portal/property-tax/owners', icon: <People sx={{ fontSize: 18 }} /> },
        { name: 'Tax Reports', path: '/portal/property-tax/reports', icon: <Assessment sx={{ fontSize: 18 }} /> },
        { name: 'Custom Reports', path: '/portal/property-tax/custom-reports', icon: <Description sx={{ fontSize: 18 }} /> },
        { name: 'Tax Settings', path: '/portal/property-tax/settings', icon: <Settings sx={{ fontSize: 18 }} /> }
      ]
    },
    {
      id: 'e-logbook',
      name: 'e-LogBook System',
      badge: 'Tracking',
      color: '#0d9488',
      bgLight: '#f0fdfa',
      icon: <MenuBook sx={{ fontSize: 20 }} />,
      basePath: '/portal/e-logbook',
      pages: [
        { name: 'Dashboard', path: '/portal/e-logbook/home', icon: <Dashboard sx={{ fontSize: 18 }} /> },
        { name: 'Document Tracking', path: '/portal/e-logbook/tracking', icon: <FactCheck sx={{ fontSize: 18 }} /> },
        { name: 'LogBook Reports', path: '/portal/e-logbook/reports', icon: <Assessment sx={{ fontSize: 18 }} /> },
        { name: 'BIR Forms & 2307', path: '/portal/e-logbook/bir-forms', icon: <Description sx={{ fontSize: 18 }} /> },
        { name: 'Suppliers Directory', path: '/portal/e-logbook/suppliers', icon: <PeopleAltIcon sx={{ fontSize: 18 }} /> },
        { name: 'LogBook Settings', path: '/portal/e-logbook/settings', icon: <Settings sx={{ fontSize: 18 }} /> }
      ]
    },
    {
      id: 'business-permit',
      name: 'Business Permit (BPLO)',
      badge: 'Licensing',
      color: '#d97706',
      bgLight: '#fffbeb',
      icon: <BusinessCenter sx={{ fontSize: 20 }} />,
      basePath: '/portal/business-permit',
      pages: [
        { name: 'Dashboard', path: '/portal/business-permit/dashboard', icon: <Dashboard sx={{ fontSize: 18 }} /> },
        { name: 'Business Applicants', path: '/portal/business-permit/applicants', icon: <Store sx={{ fontSize: 18 }} /> },
        { name: 'Permit Applications', path: '/portal/business-permit/applications', icon: <Badge sx={{ fontSize: 18 }} /> },
        { name: 'BPLO Reports', path: '/portal/business-permit/reports', icon: <Assessment sx={{ fontSize: 18 }} /> },
        { name: 'Permit Issuance', path: '/portal/business-permit/permit', icon: <Description sx={{ fontSize: 18 }} /> },
        { name: 'Regulatory Charges', path: '/portal/business-permit/charges', icon: <Payment sx={{ fontSize: 18 }} /> },
        { name: 'BPLO Settings', path: '/portal/business-permit/settings', icon: <Settings sx={{ fontSize: 18 }} /> }
      ]
    },
    {
      id: 'rural-health',
      name: 'Rural Health Unit',
      badge: 'RHU',
      color: '#e11d48',
      bgLight: '#fff1f2',
      icon: <HealthAndSafety sx={{ fontSize: 20 }} />,
      basePath: '/portal/rural-health',
      pages: [
        { name: 'Dashboard', path: '/portal/rural-health/dashboard', icon: <Dashboard sx={{ fontSize: 18 }} /> },
        { name: 'Health Services', path: '/portal/rural-health/services', icon: <MedicalServices sx={{ fontSize: 18 }} /> },
        { name: 'Order of Payments', path: '/portal/rural-health/order-payments', icon: <Receipt sx={{ fontSize: 18 }} /> },
        { name: 'Health Reports', path: '/portal/rural-health/report', icon: <Assessment sx={{ fontSize: 18 }} /> },
        { name: 'RHU Settings', path: '/portal/rural-health/settings', icon: <Settings sx={{ fontSize: 18 }} /> }
      ]
    },
    {
      id: 'civil-registry',
      name: 'Civil Registry (MCR)',
      badge: 'Records',
      color: '#0284c7',
      bgLight: '#f0f9ff',
      icon: <CardMembership sx={{ fontSize: 20 }} />,
      basePath: '/portal/civil-registry',
      pages: [
        { name: 'Dashboard', path: '/portal/civil-registry/home', icon: <Dashboard sx={{ fontSize: 18 }} /> },
        { name: 'Registry Services', path: '/portal/civil-registry/services', icon: <Assignment sx={{ fontSize: 18 }} /> },
        { name: 'Order of Payments', path: '/portal/civil-registry/orders', icon: <Receipt sx={{ fontSize: 18 }} /> },
        { name: 'Registry Reports', path: '/portal/civil-registry/reports', icon: <Assessment sx={{ fontSize: 18 }} /> },
        { name: 'Registry Settings', path: '/portal/civil-registry/settings', icon: <Settings sx={{ fontSize: 18 }} /> }
      ]
    },
    {
      id: 'settings',
      name: 'System Administration',
      badge: 'Admin',
      color: '#475569',
      bgLight: '#f8fafc',
      icon: <Settings sx={{ fontSize: 20 }} />,
      basePath: '/portal/settings',
      pages: [
        { name: 'User Management', path: '/portal/settings/users', icon: <People sx={{ fontSize: 18 }} /> },
        { name: 'Departments', path: '/portal/settings/departments', icon: <Business sx={{ fontSize: 18 }} /> },
        { name: 'Offline Backup', path: '/portal/settings/offline', icon: <CloudDownload sx={{ fontSize: 18 }} /> }
      ]
    }
  ];

  // Determine which system is active by path
  const currentPath = location.pathname;
  const activeSystem = systems.find(sys => currentPath.startsWith(sys.basePath));

  // Single expanded system state (expand one system at a time)
  const [expandedSystemId, setExpandedSystemId] = useState(() => {
    return activeSystem ? activeSystem.id : null;
  });

  useEffect(() => {
    if (activeSystem) {
      setExpandedSystemId(activeSystem.id);
    }
  }, [currentPath]);

  const toggleSystem = (systemId) => {
    setExpandedSystemId(prev => (prev === systemId ? null : systemId));
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (onCloseMobile) {
      onCloseMobile();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#ffffff' }}>
      {/* Sidebar Header */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #e2e8f0' }}>
        <Box 
          sx={{ 
            width: 42, 
            height: 42, 
            borderRadius: 2.5, 
            bgcolor: activeSystem ? activeSystem.color : '#059669', 
            color: '#ffffff', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: activeSystem ? `0 4px 10px ${activeSystem.color}40` : '0 4px 10px rgba(5, 150, 105, 0.25)',
            flexShrink: 0,
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {activeSystem ? activeSystem.icon : <Apps sx={{ fontSize: 24 }} />}
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} color="#0f172a" lineHeight={1.15} noWrap>
            LGU Concepcion
          </Typography>
          <Typography variant="caption" sx={{ color: activeSystem ? activeSystem.color : '#059669', fontWeight: 700, letterSpacing: '0.02em', display: 'block' }} noWrap>
            {activeSystem ? activeSystem.name : 'Municipal ERP & Portal'}
          </Typography>
        </Box>
      </Box>

      {/* Main Systems Portal Hub Link */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <ListItemButton
          onClick={() => handleNavigate('/portal')}
          sx={{
            borderRadius: 2.5,
            py: 0.9,
            px: 1.5,
            bgcolor: currentPath === '/portal' ? '#059669' : '#f8faf9',
            border: '1px solid',
            borderColor: currentPath === '/portal' ? '#059669' : '#e2e8f0',
            color: currentPath === '/portal' ? '#ffffff' : '#475569',
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              bgcolor: currentPath === '/portal' ? '#047857' : '#f1f5f9',
              borderColor: '#cbd5e1',
              color: '#0f172a'
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 28, color: currentPath === '/portal' ? '#ffffff' : '#059669' }}>
            <Apps sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="All Systems Portal"
            primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 700 }}
          />
        </ListItemButton>
      </Box>

      {/* Navigation Title */}
      <Box sx={{ px: 2.5, pt: 1, pb: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}>
          Municipal Systems & Modules
        </Typography>
      </Box>

      {/* Accordion Menu with All Systems (Expanded one at a time) */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1.5, py: 0.5 }}>
        <List disablePadding>
          {systems
            .filter((system) => {
              if (isSuperAdmin) return true;
              if (allowedSystems.includes(system.id)) return true;
              if (currentPath.startsWith(system.basePath)) return true;
              return false;
            })
            .map((system) => {
              const isSystemActive = currentPath.startsWith(system.basePath);
              const isExpanded = expandedSystemId === system.id;

              return (
                <Box key={system.id} sx={{ mb: 0.75 }}>
                  <ListItemButton
                    onClick={() => toggleSystem(system.id)}
                    sx={{
                      borderRadius: 2.5,
                      py: 1,
                      px: 1.5,
                      bgcolor: isSystemActive ? system.bgLight : isExpanded ? '#f8fafc' : 'transparent',
                      border: '1px solid',
                      borderColor: isSystemActive ? `${system.color}40` : isExpanded ? '#cbd5e1' : 'transparent',
                      transition: 'all 0.15s ease-in-out',
                      '&:hover': {
                        bgcolor: system.bgLight,
                        borderColor: `${system.color}30`
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, color: system.color }}>
                      {system.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: isSystemActive ? system.color : '#334155' }}>
                          {system.name}
                        </Typography>
                      }
                    />
                    {isExpanded ? (
                      <KeyboardArrowUp sx={{ fontSize: 18, color: system.color }} />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 18, color: '#94a3b8' }} />
                    )}
                  </ListItemButton>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 1.5, pt: 0.5, pb: 0.5 }}>
                      {system.pages.map((page) => {
                        const isPageSelected = currentPath === page.path;
                        return (
                          <ListItemButton
                            key={page.path}
                            onClick={() => handleNavigate(page.path)}
                            sx={{
                              borderRadius: 2,
                              py: 0.75,
                              px: 1.5,
                              my: 0.3,
                              bgcolor: isPageSelected ? `${system.color} !important` : 'transparent',
                              color: isPageSelected ? '#ffffff !important' : '#475569',
                              transition: 'all 0.15s ease-in-out',
                              '&:hover': {
                                bgcolor: isPageSelected ? system.color : system.bgLight,
                                color: isPageSelected ? '#ffffff' : system.color
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28, color: isPageSelected ? '#ffffff !important' : system.color }}>
                              {page.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={page.name}
                              primaryTypographyProps={{ fontSize: '0.81rem', fontWeight: isPageSelected ? 700 : 600 }}
                            />
                            {isPageSelected && (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: '#ffffff',
                                  ml: 1
                                }}
                              />
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
        </List>
      </Box>

      {/* User Footer Profile & Actions */}
      <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8faf9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Avatar 
              sx={{ 
                width: 38, 
                height: 38, 
                bgcolor: '#059669', 
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.875rem'
              }}
            >
              {user?.firstname?.[0] || user?.email?.[0] || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} color="#0f172a" noWrap>
                {user?.firstname ? `${user.firstname} ${user.lastname || ''}` : user?.email || 'User Account'}
              </Typography>
              <Chip 
                label={user?.role || 'Staff'} 
                size="small" 
                sx={{ 
                  height: 18, 
                  fontSize: '0.65rem', 
                  fontWeight: 700, 
                  bgcolor: '#e2e8f0', 
                  color: '#475569',
                  textTransform: 'capitalize'
                }} 
              />
            </Box>
          </Box>

          <Stack direction="row" spacing={0.5}>
            {onOpenProfile && (
              <Tooltip title="Profile Account">
                <IconButton size="small" onClick={onOpenProfile} sx={{ color: '#64748b', '&:hover': { color: '#059669', bgcolor: '#ffffff' } }}>
                  <Person sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {onLogout && (
              <Tooltip title="Sign Out">
                <IconButton size="small" onClick={onLogout} sx={{ color: '#64748b', '&:hover': { color: '#dc2626', bgcolor: '#fee2e2' } }}>
                  <Logout sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default UnifiedSidebar;
